/* Coty Optique — tuiles catégories animées, à partir des photos en place
   « soleil » : les herbes de la photo et leurs ombres ondulent au vent (rafales, renforcées au passage du curseur) ;
   « vue »    : colonnes de lettres qui défilent derrière la paire, nettes seulement à travers les verres ;
   « histoire » : la lettre de la maison vole et se pose sur un plancher, une paire vient se poser sur son coin. */
(() => {
  'use strict';
  const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- tuile « soleil » ---------------- */
  // la photo est redessinée en WebGL : seules les zones du masque (herbes et leurs ombres) ondulent au vent
  const VS = 'attribute vec2 p;varying vec2 uv;void main(){uv=vec2(p.x*.5+.5,.5-p.y*.5);gl_Position=vec4(p,0.,1.);}';
  const FS = `precision mediump float;varying vec2 uv;uniform sampler2D img,msk;uniform float t,g,asp;
float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+1.),f.x),f.y);}
float fbm(vec2 p){return .55*n(p)+.3*n(p*2.03+7.1)+.15*n(p*4.1+3.7);}
void main(){
  float m=texture2D(msk,uv).r;
  vec2 q=uv*vec2(1.,asp);
  float big=fbm(q*1.8+vec2(-t*.32,t*.07))-.5;
  float sway=.6*sin(t*1.55+q.y*2.6-q.x*1.8)+.4*sin(t*.83+q.y*1.2+1.3);
  float fl=n(q*11.+vec2(-t*2.2,t*.4))-.5;
  vec2 dir=normalize(vec2(1.,-.3));
  vec2 d=dir*g*(.5*sway+.9*big)+vec2(fl,fl*.6)*.28*g;
  gl_FragColor=texture2D(img,uv-d*m*vec2(.011,.011/asp));
}`;
  function sun(tile) {
    const cv = tile.querySelector('[data-sun-wind]');
    if (!cv || reduced) return;
    const gl = cv.getContext('webgl', { premultipliedAlpha: false, antialias: false });
    if (!gl) return;
    const load = src => new Promise((ok, ko) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => ok(i); i.onerror = ko; i.src = src; });
    Promise.all([load(cv.dataset.src), load(cv.dataset.mask)]).then(([im, mk]) => {
      const sh = (type, src) => { const o = gl.createShader(type); gl.shaderSource(o, src); gl.compileShader(o); return o; };
      const pr = gl.createProgram(); gl.attachShader(pr, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(pr);
      if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) return;
      gl.useProgram(pr);
      const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      const tex = (unit, el, name) => {
        const t = gl.createTexture(); gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(gl.TEXTURE_2D, t);
        [[gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE], [gl.TEXTURE_MIN_FILTER, gl.LINEAR], [gl.TEXTURE_MAG_FILTER, gl.LINEAR]].forEach(([k, v]) => gl.texParameteri(gl.TEXTURE_2D, k, v));
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, el);
        gl.uniform1i(gl.getUniformLocation(pr, name), unit);
      };
      try { tex(0, im, 'img'); tex(1, mk, 'msk'); } catch (e) { return; }   // image non partageable : la photo fixe reste affichée
      const uT = gl.getUniformLocation(pr, 't'), uG = gl.getUniformLocation(pr, 'g');
      gl.uniform1f(gl.getUniformLocation(pr, 'asp'), im.naturalHeight / im.naturalWidth);
      const size = () => {
        const tw = tile.clientWidth, th = tile.clientHeight, k = Math.max(tw / im.naturalWidth, th / im.naturalHeight);
        const w = Math.min(im.naturalWidth, Math.round(im.naturalWidth * k * Math.min(1.25, devicePixelRatio || 1)));
        cv.width = w; cv.height = Math.round(w * im.naturalHeight / im.naturalWidth); gl.viewport(0, 0, cv.width, cv.height);
      };
      size(); addEventListener('resize', size);
      // rafales : souffle de fond irrégulier, renforcé quand le curseur ou le doigt balaie la tuile
      let gust = 0, push = 0, last = null, raf = 0, visible = false; const t0 = performance.now();
      tile.addEventListener('pointermove', e => {
        if (last) push = Math.min(1.4, push + Math.hypot(e.clientX - last.x, e.clientY - last.y) / 160);
        last = { x: e.clientX, y: e.clientY };
      });
      tile.addEventListener('pointerleave', () => { last = null; });
      let lastDraw = 0;
      const loop = now => {
        raf = 0;
        // effet d'ambiance : 30 images/s suffisent, et rien pendant qu'on interagit avec la page
        if (now - lastDraw < 33 || now - (window.__cotyBusy || 0) < 250) { if (visible) raf = requestAnimationFrame(loop); return; }
        lastDraw = now;
        const t = (now - t0) / 1000;
        const base = .55 + .3 * Math.sin(t * .47) * Math.sin(t * .19 + 1) + .15 * Math.sin(t * 1.3);
        push *= .965; gust = lerp(gust, base + push, .06);
        gl.uniform1f(uT, t); gl.uniform1f(uG, gust);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        if (!tile.classList.contains('is-live')) tile.classList.add('is-live');
        if (visible) raf = requestAnimationFrame(loop);
      };
      new IntersectionObserver(es => { visible = es[0].isIntersecting; if (visible && !raf) raf = requestAnimationFrame(loop); }).observe(tile);
    }).catch(() => {});
  }

  /* ---------------- tuile « vue » ---------------- */
  const OPTO = 'EFPTOZLPEDPECFDEDFCZPFELOPZDDEFPOTEC';
  function vision(tile) {
    const blur = tile.querySelector('[data-vis-blur]');
    const sharp = tile.querySelector('[data-vis-sharp]');
    const build = () => {
      const W = tile.clientWidth, colW = Math.max(22, W / 8), n = Math.ceil(W / colW) + 1;
      tile.style.setProperty('--vis-fs', (colW * .62).toFixed(1) + 'px');
      let html = '';
      for (let c = 0; c < n; c++) {
        let letters = '';
        for (let i = 0; i < 14; i++) letters += `<span>${OPTO[(c * 7 + i * 5) % OPTO.length]}</span>`;
        html += `<span class="vis__col" style="width:${colW.toFixed(1)}px;animation-duration:${16 + (c % 3) * 4}s">${letters}${letters}</span>`;
      }
      blur.innerHTML = html; sharp.innerHTML = html;
    };
    build();
    let rt, lastW = tile.clientWidth;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { if (tile.clientWidth !== lastW) { lastW = tile.clientWidth; build(); } }, 150); });
  }

  /* ---------------- tuile « histoire » ---------------- */
  // lettre de la maison qui vole et se pose sur un plancher (bois en WebGL, ombres de feuillage), puis une paire vient se poser sur le coin
  const WOOD = ['precision highp float;uniform vec2 R;uniform float S;',
    'float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}',
    'float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}',
    'float fb(vec2 p){float v=0.,a=.5;for(int i=0;i<5;i++){v+=a*n(p);p=p*2.03+17.1;a*=.5;}return v;}',
    'void main(){vec2 c=vec2(gl_FragCoord.x,R.y-gl_FragCoord.y)/S;',
    'float PH=230.;float pi=floor(c.y/PH);float ly=c.y-pi*PH;',
    'float L=1150.+h(vec2(pi,3.))*500.;float off=h(vec2(pi,9.))*L;float sx=c.x+off;float j=floor(sx/L);float lx=sx-j*L;',
    'float r=h(vec2(pi,j));float r2=h(vec2(j,pi+4.));',
    'vec2 q=vec2(lx,ly+r*900.);',
    'float w=fb(vec2(q.x*.0007,q.y*.0035)+r*31.)*2.6+fb(vec2(q.x*.0025,q.y*.012)+r2*9.)*.7;',
    'float cx=L*(.3+.4*r2);float cat=pow(abs(lx-cx)/L,2.)*(6.+r*10.)*step(.45,r);',
    'float ring=q.y*.03+w*1.6-cat*(1.+.5*sin(q.y*.01));',
    'float rf=fract(ring);float band=smoothstep(.0,.45,rf)*smoothstep(1.,.55,rf);float late=smoothstep(.86,.985,rf)+smoothstep(.035,0.,rf);float r3=fract(ring*3.7);',
    'float fib=fb(vec2(q.x*.006,q.y*.9)+r*3.);float fib2=n(vec2(q.x*.02,q.y*2.6));',
    'vec3 dk=vec3(.29,.175,.10);vec3 md=vec3(.45,.30,.18);vec3 lt=vec3(.56,.39,.245);',
    'vec3 col=mix(md*.92,lt,band*.6);col=mix(col,dk,late*.42);col*=.96+.05*smoothstep(.8,1.,r3);col=mix(col,col*.78,smoothstep(.5,.85,fib)*.6);',
    'float pore=smoothstep(.72,.95,n(vec2(q.x*.08,q.y*1.7)+r));col*=1.-pore*.18;',
    'col*=.88+fib2*.14;col*=.92+.16*fb(vec2(c.x*.0012,c.y*.003)+r*5.);',
    'col*=mix(vec3(1.),vec3(1.07,1.0,.92),r)*(0.9+r2*.16);',
    'float kx=L*(.25+.5*r2),ky=PH*(.3+.4*r);vec2 kd=vec2((lx-kx)*.55,ly-ky);float kr=length(kd);',
    'if(r2>.62){float kn=smoothstep(26.,4.,kr);float kring=sin(kr*.55-fb(kd*.05)*4.)*.5+.5;col=mix(col,dk*.75*(.8+.3*kring),kn*.85);col*=1.-smoothstep(70.,0.,kr)*.12*(1.-kn);}',
    'float e=min(ly,PH-ly);col*=mix(.32,1.,smoothstep(0.,2.2,e));col*=mix(.82,1.,smoothstep(2.,10.,e));col+=vec3(.04,.025,.01)*smoothstep(3.,1.,abs(ly-3.));',
    'float ej=min(lx,L-lx);col*=mix(.4,1.,smoothstep(0.,1.8,ej));col*=mix(.88,1.,smoothstep(1.,7.,ej));',
    'gl_FragColor=vec4(pow(col,vec3(1.04)),1.);}'].join('\n');
  function letter(tile) {
    const root = tile.querySelector('[data-lt]'); if (!root) return;
    const wood = root.querySelector('[data-lt-wood]'), fly = root.querySelector('[data-lt-fly]'), paper = root.querySelector('[data-lt-paper]');
    const shadow = root.querySelector('[data-lt-shadow]'), bend = root.querySelector('[data-lt-bend]'), light = root.querySelector('[data-lt-light]');
    // plancher
    let gl = null, uR, uS;
    try { gl = wood.getContext('webgl', { preserveDrawingBuffer: true }); } catch (e) {}
    if (gl) {
      const sh = (t, s) => { const o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); return o; };
      const pr = gl.createProgram(); gl.attachShader(pr, sh(gl.VERTEX_SHADER, 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}')); gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, WOOD)); gl.linkProgram(pr);
      if (gl.getProgramParameter(pr, gl.LINK_STATUS)) {
        gl.useProgram(pr);
        const b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
        const a = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(a); gl.vertexAttribPointer(a, 2, gl.FLOAT, false, 0, 0);
        uR = gl.getUniformLocation(pr, 'R'); uS = gl.getUniformLocation(pr, 'S');
      } else gl = null;
    }
    const drawWood = () => {
      if (!gl) return;
      const dpr = Math.min(devicePixelRatio || 1, 2), w = root.clientWidth, h = root.clientHeight, W = Math.round(w * dpr), H = Math.round(h * dpr);
      if (!W || (wood.width === W && wood.height === H)) return;
      wood.width = W; wood.height = H; gl.viewport(0, 0, W, H); gl.uniform2f(uR, W, H); gl.uniform1f(uS, dpr * Math.max(.3, w / 1000)); gl.drawArrays(gl.TRIANGLES, 0, 6);
    };
    // ombres de feuillage : grappes de feuilles floues, figées en image puis balancées en CSS
    const leaves = light.querySelector('[data-lt-leaves]');
    if (leaves) {
      let s = 7; const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647, ns = 'http://www.w3.org/2000/svg';
      [[1080, 170, 230], [900, 30, 160], [1150, 500, 190], [760, 140, 110], [1120, 880, 170], [260, 1180, 140]].forEach(c => {
        const g = document.createElementNS(ns, 'g');
        for (let i = 0, n = Math.round(c[2] / 4.2); i < n; i++) {
          const a = rnd() * 6.283, rad = Math.sqrt(rnd()) * c[2], u = document.createElementNS(ns, 'use');
          u.setAttribute('href', '#ltLf');
          u.setAttribute('transform', `translate(${(c[0] + Math.cos(a) * rad).toFixed(1)} ${(c[1] + Math.sin(a) * rad * .8).toFixed(1)}) rotate(${(rnd() * 360).toFixed(0)}) scale(${(.9 + rnd() * 1.4).toFixed(2)})`);
          u.setAttribute('opacity', (.55 + rnd() * .45).toFixed(2)); g.appendChild(u);
        }
        for (let k = 0; k < 3; k++) {
          const p = document.createElementNS(ns, 'path'), ax = c[0] + (rnd() - .5) * c[2], ay = c[1] + (rnd() - .5) * c[2];
          p.setAttribute('d', `M${ax} ${ay} Q ${ax + 60} ${ay + 40} ${ax + 150 * (rnd() + .3)} ${ay - 80 * rnd()}`);
          p.setAttribute('stroke', '#2e2016'); p.setAttribute('stroke-width', '5'); p.setAttribute('fill', 'none'); g.appendChild(p);
        }
        leaves.appendChild(g);
      });
    }
    const bake = () => light.querySelectorAll('svg').forEach(sv => {
      const host = sv.parentNode, w = Math.round(root.clientWidth * 1.3 * .6), h = Math.round(root.clientHeight * 1.3 * .6);
      if (!w || !h) return;
      const c = sv.cloneNode(true); c.setAttribute('xmlns', 'http://www.w3.org/2000/svg'); c.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
      c.setAttribute('width', w); c.setAttribute('height', h); c.removeAttribute('style');
      c.querySelectorAll('use').forEach(u => u.setAttribute('xlink:href', u.getAttribute('href')));
      const im = new Image();
      im.onload = () => { const cv = document.createElement('canvas'); cv.width = w; cv.height = h; cv.getContext('2d').drawImage(im, 0, 0, w, h); const old = host.querySelector('canvas'); if (old) old.remove(); host.appendChild(cv); sv.style.display = 'none'; };
      im.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(c));
    });
    // feuille qui se courbe pendant le vol : bandes horizontales articulées
    const N = 14, MID = N / 2; let segsD = [], segsU = [], shD = [], shU = [], gRx = 0;
    const buildBend = () => {
      bend.innerHTML = ''; segsD = []; segsU = []; shD = []; shU = [];
      const H = paper.offsetHeight, h = H / N, tpl = paper.cloneNode(true); tpl.removeAttribute('data-lt-paper');
      const face = i => { const f = document.createElement('span'); f.className = 'lt__face'; f.style.height = (h + 1.5) + 'px'; const c = tpl.cloneNode(true); c.style.top = (-i * h) + 'px'; f.appendChild(c); const s = document.createElement('span'); s.className = 'lt__shd'; f.appendChild(s); return [f, s]; };
      let par = bend;
      for (let i = MID; i < N; i++) { const w = document.createElement('span'); w.className = 'lt__seg'; w.style.height = h + 'px'; w.style.top = (i === MID ? MID * h : h) + 'px'; w.style.transformOrigin = '50% 0'; const fs = face(i); w.appendChild(fs[0]); par.appendChild(w); par = w; segsD.push(w); shD.push(fs[1]); }
      par = bend;
      for (let i = MID - 1; i >= 0; i--) { const w = document.createElement('span'); w.className = 'lt__seg'; w.style.height = h + 'px'; w.style.top = (i === MID - 1 ? (MID - 1) * h : -h) + 'px'; w.style.transformOrigin = '50% 100%'; const fs = face(i); w.appendChild(fs[0]); par.appendChild(w); par = w; segsU.push(w); shU.push(fs[1]); }
    };
    const col = o => { const a = Math.min(.5, Math.abs(Math.sin(o * Math.PI / 180)) * .55); return o > 0 ? `rgba(45,28,12,${a.toFixed(3)})` : `rgba(255,252,244,${(a * .5).toFixed(3)})`; };
    const grad = (el, a, b) => { el.style.background = `linear-gradient(${col(a)},${col(b)})`; };
    const setBend = (amp, t) => {
      let cD = 0, cU = 0; const n = segsD.length, oD = [], oU = [];
      for (let j = 0; j < n; j++) {
        const s = (j + .5) / n, cup = amp * 3.6 * Math.sin(t * 5.1 + .8);
        const wD = amp * 4.2 * Math.sin(t * 9.2 - s * 2.4 + .3) * (.5 + s), wU = amp * 4.2 * Math.sin(t * 8.1 - s * 2.2 + 2.1) * (.5 + s);
        const aD = cup + wD, aU = -(cup + wU); cD += aD; cU += aU; oD.push(cD + gRx); oU.push(-cU + gRx);
        segsD[j].style.transform = `rotateX(${aD.toFixed(2)}deg)`; segsU[j].style.transform = `rotateX(${aU.toFixed(2)}deg)`;
      }
      for (let j = 0; j < n; j++) {
        let p = j ? oD[j - 1] : (oD[0] + oU[0]) / 2, q = j < n - 1 ? oD[j + 1] : oD[j]; grad(shD[j], (p + oD[j]) / 2, (q + oD[j]) / 2);
        p = j ? oU[j - 1] : (oD[0] + oU[0]) / 2; q = j < n - 1 ? oU[j + 1] : oU[j]; grad(shU[j], (q + oU[j]) / 2, (p + oU[j]) / 2);
      }
    };
    const ease = t => 1 - Math.pow(1 - t, 3);
    const pose = t => {
      const land = .8, hgt = t < land ? Math.pow(1 - t / land, 1.35) : 0, u = Math.min(t / land, 1), e = ease(u), k = 1 - u;
      const vw = root.clientWidth, vh = root.clientHeight;
      let x = -vw * .6 * (1 - e) + Math.sin(u * Math.PI * 2.1) * vw * .12 * k, y = -vh * 1.05 * (1 - Math.pow(u, .8)) + Math.sin(u * Math.PI * 4.2) * vh * .03 * k;
      let rz = -28 * k * k + Math.sin(u * Math.PI * 2.1 + .6) * 16 * k, rx = Math.sin(u * Math.PI * 4.2 + .4) * 34 * Math.pow(k, 1.2), ry = Math.sin(u * Math.PI * 2.1 - .3) * 26 * Math.pow(k, 1.1), amp;
      if (t >= land) { const s = (t - land) / (1 - land), sl = 1 - Math.pow(1 - s, 3); x = -vw * .012 * (1 - sl) * Math.cos(s * 2); y = 0; rx = Math.sin(s * Math.PI * 2) * 1.4 * (1 - s); rz = -1.2 * (1 - ease(s)); ry = 0; amp = .35 * Math.pow(1 - s, 2) * Math.cos(s * 7); }
      else amp = .45 + .55 * Math.pow(k, .7);
      return { x, y, rx, ry, rz, sc: 1 + .36 * hgt, h: hgt, amp };
    };
    const apply = (p, t) => {
      gRx = p.rx; const vw = root.clientWidth / 1300, h = p.h;
      fly.style.transform = `translate3d(${p.x.toFixed(1)}px,${p.y.toFixed(1)}px,0) rotateZ(${p.rz.toFixed(2)}deg) rotateX(${p.rx.toFixed(2)}deg) rotateY(${p.ry.toFixed(2)}deg) scale(${p.sc.toFixed(4)})`;
      shadow.style.transform = `translate(${(p.x + 3 - h * 170 * vw * 2).toFixed(1)}px,${(p.y + 6 + h * 260 * vw * 2).toFixed(1)}px) rotate(${p.rz.toFixed(2)}deg) scale(${(1 - h * .18).toFixed(3)},${(1 - h * .18 - Math.abs(p.amp) * .06).toFixed(3)})`;
      shadow.style.filter = `blur(${(4 + h * 26).toFixed(1)}px)`; shadow.style.opacity = (1 - h * .42).toFixed(3);
      if (segsD.length) setBend(p.amp, t * 4.3);
    };
    const flat = on => { bend.style.display = on ? 'none' : 'block'; paper.style.visibility = on ? 'visible' : 'hidden'; };
    let played = false;
    const play = () => {
      if (played) return; played = true;
      const lite = root.clientWidth < 360 || matchMedia('(pointer: coarse)').matches;
      if (!lite) { buildBend(); flat(false); }
      apply(pose(0), 0);
      const D = 3600; let t0 = null;
      const f = ts => {
        if (!t0) t0 = ts; const t = Math.min((ts - t0) / D, 1); apply(pose(t), t);
        if (t < 1) requestAnimationFrame(f);
        else { flat(true); bend.innerHTML = ''; segsD = []; segsU = []; setTimeout(() => root.classList.add('is-done'), 150); }
      };
      requestAnimationFrame(f);
    };
    drawWood(); bake();
    let rt; addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { drawWood(); bake(); }, 200); });
    if (reduced) { root.classList.add('is-done'); return; }
    root.classList.add('is-armed'); apply(pose(0), 0);
    const sec = tile.closest('.tiles');
    if (sec && sec.classList.contains('tiles--reveal') && !tile.classList.contains('is-in')) tile.addEventListener('tile:shown', () => setTimeout(play, 150), { once: true });
    else new IntersectionObserver((es, io) => { if (es[0].isIntersecting) { io.disconnect(); setTimeout(play, 250); } }, { threshold: .45 }).observe(root);
  }

  /* ---------------- apparition des tuiles, une par une, à l'arrivée sur la section ---------------- */
  function reveal(sec) {
    if (reduced || !('IntersectionObserver' in window)) return;
    const tiles = [...sec.querySelectorAll('.tile')];
    if (sec.getBoundingClientRect().top < innerHeight * .6) return;   // déjà à l'écran au chargement : rien à masquer
    sec.classList.add('tiles--reveal');
    // chaque tuile apparaît quand ELLE arrive à l'écran (sur mobile, la 2e rangée attend son tour) ;
    // les tuiles qui arrivent ensemble se succèdent, une par une
    let last = 0;
    const io = new IntersectionObserver(es => {
      const now = performance.now();
      let k = Math.max(0, Math.ceil((last - now) / 220));
      es.filter(e => e.isIntersecting).sort((x, y) => tiles.indexOf(x.target) - tiles.indexOf(y.target)).forEach(e => {
        const t = e.target, d = 120 + k * 220; k++; io.unobserve(t);
        last = now + d;
        t.style.setProperty('--d', d + 'ms');
        requestAnimationFrame(() => t.classList.add('is-in'));
        setTimeout(() => t.dispatchEvent(new CustomEvent('tile:shown')), d + 650);
      });
    }, { threshold: .25, rootMargin: '0px 0px -12% 0px' });
    tiles.forEach(t => io.observe(t));
  }

  const run = () => {
    document.querySelectorAll('.tiles').forEach(reveal);
    document.querySelectorAll('[data-tile-sun]').forEach(sun);
    document.querySelectorAll('[data-tile-vision]').forEach(vision);
    document.querySelectorAll('[data-tile-letter]').forEach(letter);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();
